<template>
  <div class="approche-page">
    <AppHead />

    <section class="hero-section">
      <div class="hero-media" aria-hidden="true">
        <img
          src="@/assets/img/advisory-meeting.png"
          width="1440"
          height="1088"
          alt=""
          class="hero-media__img"
          fetchpriority="high"
          decoding="async"
        />
      </div>
      <div class="hero-scrim" aria-hidden="true"></div>
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">Ce qui se passe dans vos 90 premiers jours</h1>
          <p class="hero-lead">
            Une méthode claire pour consolider, structurer et transmettre avec un conseiller dédié.
          </p>
          <div class="hero-actions">
            <router-link
              to="/diagnostic"
              class="btn-brand btn-brand--primary"
              @click="trackCta('approche_hero', 'Faire mon diagnostic')"
            >
              Faire mon diagnostic
            </router-link>
            <router-link
              to="/approche#offres"
              class="btn-brand btn-brand--on-dark"
              @click="trackCta('approche_hero', 'Voir les offres')"
            >
              Voir les offres
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <section class="philosophie-section section-padding">
      <div class="container">
        <div class="section-header text-center mb-5">
          <h2 class="section-title">Pourquoi 3 patrimoines sur 4 perdent de la valeur à la transmission</h2>
        </div>
        <div class="row align-items-center g-5">
          <div class="col-lg-6">
            <div class="philosophy-image-wrapper">
              <img src="@/assets/img/conseiller-patrimonial.png" width="720" height="544" alt="Conseillère patrimoniale en cabinet" class="img-fluid rounded-4 shadow-lg" loading="lazy">
            </div>
          </div>
          <div class="col-lg-6">
            <div class="philosophy-content">
              <p class="content-text">
                Nous croyons qu'un patrimoine bien géré repose sur trois piliers essentiels :
                la clarté dans les décisions, la discipline dans l'exécution,
                et une vision à long terme.
              </p>
              <p class="content-text">
                Notre rôle est de vous accompagner pour structurer vos décisions patrimoniales,
                sans pression commerciale et sans conflit d'intérêts.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="approche-steps section-padding">
      <div class="container">
        <div class="section-header steps-header">
          <p class="steps-eyebrow">Méthode</p>
          <h2 class="section-title steps-title">Ce que vous obtenez à chaque étape</h2>
          <span class="rule-brown" aria-hidden="true" />
        </div>
        <div class="steps-grid">
          <article class="step-card">
            <span class="step-number" aria-hidden="true">01</span>
            <h3 class="step-title">Vision consolidée</h3>
            <p class="step-desc">
              Inventaire complet de vos actifs, dettes et engagements, puis lecture claire de votre situation.
            </p>
          </article>
          <article class="step-card">
            <span class="step-number" aria-hidden="true">02</span>
            <h3 class="step-title">Priorités &amp; plan</h3>
            <p class="step-desc">
              Trois leviers prioritaires chiffrés, arbitrés avec vous et documentés.
            </p>
          </article>
          <article class="step-card">
            <span class="step-number" aria-hidden="true">03</span>
            <h3 class="step-title">Mise en œuvre</h3>
            <p class="step-desc">
              Sélection des solutions et exécution encadrée par votre conseiller dédié.
            </p>
          </article>
          <article class="step-card">
            <span class="step-number" aria-hidden="true">04</span>
            <h3 class="step-title">Tableau de bord</h3>
            <p class="step-desc">
              Suivi, ajustements et revue périodique de votre trajectoire patrimoniale.
            </p>
          </article>
        </div>
      </div>
    </section>

    <section class="pilier-section section-padding">
      <div class="container">
        <div class="pilier-grid">
          <div class="pilier-content">
            <p class="pilier-eyebrow">Pilier 1</p>
            <h2 class="pilier-title">Constitution &amp; structuration</h2>
            <span class="pilier-rule" aria-hidden="true" />
            <p class="pilier-lead">
              Nous organisons vos revenus, votre épargne et vos véhicules d’investissement pour créer une base solide et lisible.
            </p>
            <div class="pilier-chips">
              <span class="pilier-chip">Cartographie du patrimoine</span>
              <span class="pilier-chip">Séparation patrimoine professionnel & privé</span>
              <span class="pilier-chip">Épargne calibrée</span>
            </div>
          </div>
          <div class="pilier-visual">
            <img
              src="@/assets/img/dashboard-desk.png"
              width="1200"
              height="800"
              alt="Tableau de bord patrimonial sur tablette"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="pilier-section section-padding pilier-section--alt">
      <div class="container">
        <div class="pilier-grid pilier-grid--reverse">
          <div class="pilier-content">
            <p class="pilier-eyebrow">Pilier 2</p>
            <h2 class="pilier-title">Optimisation &amp; diversification</h2>
            <span class="pilier-rule" aria-hidden="true" />
            <p class="pilier-lead">
              Nous intégrons, selon votre profil et vos objectifs, différentes classes d’actifs pour renforcer la résilience de votre patrimoine.
            </p>
            <div class="pilier-chips">
              <span class="pilier-chip">Immobilier</span>
              <span class="pilier-chip">Valeurs mobilières</span>
              <span class="pilier-chip">Fonds &amp; placements</span>
              <span class="pilier-chip">Diversification géographique</span>
            </div>
          </div>
          <div class="pilier-visual">
            <img
              src="@/assets/img/analyse-financiere-africaine.png"
              width="720"
              height="544"
              alt="Équipe en analyse financière autour d’un tableau de bord"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="pilier-section section-padding">
      <div class="container">
        <div class="pilier-grid">
          <div class="pilier-content">
            <p class="pilier-eyebrow">Pilier 3</p>
            <h2 class="pilier-title">Retraite &amp; protection</h2>
            <span class="pilier-rule" aria-hidden="true" />
            <p class="pilier-lead">
              Préparer demain, c’est protéger ce que vous avez de plus essentiel : votre avenir et celui de vos proches.
            </p>
            <div class="pilier-chips">
              <span class="pilier-chip">Anticipation retraite</span>
              <span class="pilier-chip">Sécurisation des revenus</span>
              <span class="pilier-chip">Protection des proches</span>
            </div>
          </div>
          <div class="pilier-visual">
            <img
              src="@/assets/img/planification-retraite.png"
              width="720"
              height="544"
              alt="Couple préparant sereinement sa retraite"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="pilier-section section-padding pilier-section--alt">
      <div class="container">
        <div class="pilier-grid pilier-grid--reverse">
          <div class="pilier-content">
            <p class="pilier-eyebrow">Pilier 4</p>
            <h2 class="pilier-title">Transmission &amp; continuité</h2>
            <span class="pilier-rule" aria-hidden="true" />
            <p class="pilier-lead">
              Un avenir serein repose sur une transmission préparée : organisation successorale, bénéficiaires et gouvernance familiale.
            </p>
            <div class="pilier-chips">
              <span class="pilier-chip">Donation</span>
              <span class="pilier-chip">Succession</span>
              <span class="pilier-chip">Gouvernance</span>
            </div>
          </div>
          <div class="pilier-visual">
            <img
              src="@/assets/img/hero-famille-patrimoine.png"
              width="1200"
              height="800"
              alt="Famille multigénérationnelle — transmission patrimoniale"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="cta-section section-padding" id="offres">
      <div class="container">
        <div class="cta-content text-center">
          <p class="cta-kicker">Offres</p>
          <h2 class="cta-title">Choisissez l’accompagnement adapté</h2>
          <p class="cta-lead">
            Revenu mensuel net ≥ 750&nbsp;000&nbsp;FCFA
          </p>
        </div>

        <div class="offres-grid">
          <article class="offre-card">
            <header class="offre-card__head">
              <p class="offre-card__tag">Individuel</p>
              <h3 class="offre-card__title">Persona Basique</h3>
            </header>
            <p class="offre-card__split">
              190&nbsp;000 FCFA en une fois +
            </p>
            <div class="offre-card__price">
              <span class="offre-card__amount">24&nbsp;500</span>
              <span class="offre-card__currency">FCFA / mois</span>
            </div>
            <p class="offre-card__split">
              Soit 294&nbsp;000 FCFA / an
            </p>
            <ul class="offre-card__list offre-card__list--checks">
              <li>Diagnostic patrimonial complet</li>
              <li>Plan d’investissement personnalisé</li>
              <li>2 appels annuels avec votre conseiller</li>
              <li>2 projets accompagnés / an</li>
              <li>Tableau de bord 7j/7</li>
            </ul>
            <div class="offre-card__cta">
              <router-link to="/inscrire" class="btn-brand btn-brand--secondary btn-offre-cta">
                Souscrire
              </router-link>
            </div>
          </article>

          <article class="offre-card">
            <header class="offre-card__head">
              <p class="offre-card__tag">Couple</p>
              <h3 class="offre-card__title">Duo Basique</h3>
            </header>
            <p class="offre-card__split">
              360&nbsp;000 FCFA en une fois +
            </p>
            <div class="offre-card__price">
              <span class="offre-card__amount">34&nbsp;500</span>
              <span class="offre-card__currency">FCFA / mois</span>
            </div>
            <p class="offre-card__split">
              Soit 414&nbsp;000 FCFA / an
            </p>
            <ul class="offre-card__list offre-card__list--checks">
              <li>Diagnostic patrimonial complet</li>
              <li>Plan d’investissement personnalisé</li>
              <li>4 appels annuels avec votre conseiller</li>
              <li>2 projets accompagnés / an</li>
              <li>Tableau de bord 7j/7</li>
            </ul>
            <div class="offre-card__cta">
              <router-link to="/inscrire" class="btn-brand btn-brand--secondary btn-offre-cta">
                Souscrire
              </router-link>
            </div>
          </article>

          <article class="offre-card">
            <header class="offre-card__head">
              <p class="offre-card__tag">Entrepreneurs, entrepreneurs individuels, professions libérales</p>
              <h3 class="offre-card__title">Pro Basique</h3>
            </header>
            <p class="offre-card__split">
              300&nbsp;000 FCFA en une fois +
            </p>
            <div class="offre-card__price">
              <span class="offre-card__amount">400&nbsp;000</span>
              <span class="offre-card__currency">FCFA en 3 fois</span>
            </div>
            <p class="offre-card__split">Soit 400&nbsp;000 FCFA / an</p>
            <ul class="offre-card__list offre-card__list--checks">
              <li>Assistance personnalisée</li>
              <li>4 appels annuels avec votre conseiller</li>
              <li>Tableau de bord 7j/7</li>
            </ul>
            <div class="offre-card__cta">
              <router-link to="/nous-contacter?demande=autre" class="btn-brand btn-brand--secondary btn-offre-cta">
                Souscrire
              </router-link>
            </div>
          </article>
          <article class="offre-card offre-card--featured">
            <span class="offre-card__badge">Le plus choisi</span>
            <header class="offre-card__head">
              <p class="offre-card__tag">Individuel</p>
              <h3 class="offre-card__title">Persona Prémium</h3>
            </header>
            <p class="offre-card__split">
              190&nbsp;000 FCFA en une fois +
            </p>
            <div class="offre-card__price">
              <span class="offre-card__amount">28&nbsp;500</span>
              <span class="offre-card__currency">FCFA / mois</span>
            </div>
            <p class="offre-card__split">
              Soit 342&nbsp;000 FCFA / an
            </p>
            <ul class="offre-card__list offre-card__list--checks">
              <li>Offre Persona basique +</li>
              <li>Credit Scoring</li>
              <li>Téléconsultation médicale</li>
              <li>Financement projet par levée de fonds</li>
              <li>Teaser projets</li>
              <li>Teaser emprunteur</li>
              <li>Accompagnement à l'assainissement des dettes</li>
            </ul>
            <div class="offre-card__cta">
              <router-link to="/inscrire" class="btn-brand btn-brand--primary btn-offre-cta">
                Souscrire
              </router-link>
            </div>
          </article>

          <article class="offre-card">
            <header class="offre-card__head">
              <p class="offre-card__tag">Couple</p>
              <h3 class="offre-card__title">Duo Prémium</h3>
            </header>
            <p class="offre-card__split">
              360&nbsp;000 FCFA en une fois +
            </p>
            <div class="offre-card__price">
              <span class="offre-card__amount">38&nbsp;500</span>
              <span class="offre-card__currency">FCFA / mois</span>
            </div>
            <p class="offre-card__split">
              Soit 462&nbsp;000 FCFA / an
            </p>
            <ul class="offre-card__list offre-card__list--checks">
              <li>Offre Duo basique +</li>
              <li>Credit Scoring</li>
              <li>Téléconsultation médicale</li>
              <li>Financement projet par levée de fonds</li>
              <li>Teaser projets</li>
              <li>Teaser emprunteur</li>
              <li>Accompagnement à l'assainissement des dettes</li>
            </ul>
            <div class="offre-card__cta">
              <router-link to="/inscrire" class="btn-brand btn-brand--secondary btn-offre-cta">
                Souscrire
              </router-link>
            </div>
          </article>

          <article class="offre-card">
            <header class="offre-card__head">
              <p class="offre-card__tag">Entrepreneurs, entrepreneurs individuels, professions libérales</p>
              <h3 class="offre-card__title">Pro Prémium</h3>
            </header>
            <p class="offre-card__split">
              300&nbsp;000 FCFA en une fois +
            </p>
            <div class="offre-card__price">
              <span class="offre-card__amount">500&nbsp;000</span>
              <span class="offre-card__currency">FCFA en 3 fois</span>
            </div>
            <p class="offre-card__split">Soit 500&nbsp;000 FCFA / an</p>
            <ul class="offre-card__list offre-card__list--checks">
              <li>Offre Pro basique +</li>
              <li>Valorisation d'entreprise</li>
              <li>Teaser de levée de fonds / </li>
              <li>Structuration d'opération financière gratuite(levé de fonds...)</li>
              <span class="required-text"><i class="fas fa-asterisk" aria-hidden="true"></i></span><span class="required-text-text"> 2% au lieu de 3% de commission sur les levés de fonds.</span>
            </ul>
            <div class="offre-card__cta">
              <router-link to="/nous-contacter?demande=autre" class="btn-brand btn-brand--secondary btn-offre-cta">
                Souscrire
              </router-link>
            </div>
          </article>
        </div>

        <p class="cta-promo">
          <span class="required-asterisk"><i class="fas fa-asterisk" aria-hidden="true"></i></span>
          −25&nbsp;% jusqu’au 1<sup>er</sup> septembre 2026, sous conditions.
        </p>
        <div class="offres-fallback text-center mt-4">
          <p class="mb-2">Pas encore sûr ? Commencez par le diagnostic.</p>
          <router-link
            to="/diagnostic"
            class="btn-brand btn-brand--primary"
            @click="trackCta('approche_offres', 'Faire mon diagnostic')"
          >
            Faire mon diagnostic
          </router-link>
        </div>
      </div>
    </section>

    <StickyCtaBar location="approche_sticky" />
    <SiteFooter />
  </div>
</template>

<script>
import AppHead from '@/components/public/AppHead.vue';
import SiteFooter from '@/components/public/Footer.vue';
import StickyCtaBar from '@/components/public/StickyCtaBar.vue';
import { trackCtaClick } from '@/utils/analytics';

export default {
  name: 'NotreApproche',
  components: {
    AppHead,
    SiteFooter,
    StickyCtaBar,
  },
  methods: {
    trackCta(location, label) {
      trackCtaClick({ location, label });
    },
  },
  metaInfo() {
    return {
      title: 'Notre Approche Patrimoniale - GWEM',
      meta: [
        {
          name: 'description',
          content: 'Méthode GWEM : analyse, structuration, diversification, protection et transmission du patrimoine.',
        },
      ],
    };
  },
};
</script>

<style scoped>
.approche-page {
  background: var(--bank-gray-50);
  color: var(--bank-gray-900);
  font-family: var(--font-body);
  overflow-x: clip;
}

/* Fallback container padding (déploiement sans Bootstrap) */
.container {
  width: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding-left: clamp(16px, 4vw, 24px);
  padding-right: clamp(16px, 4vw, 24px);
}

.hero-section {
  background: var(--brand-navy-deep);
  color: var(--paper);
  padding: 140px 0 110px;
  text-align: center;
  position: relative;
  overflow: hidden;
  isolation: isolate;
  min-height: 420px;
  display: grid;
  align-items: center;
}

.hero-section .hero-scrim {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(
    105deg,
    rgba(84, 57, 41, 0.94) 0%,
    rgba(84, 57, 41, 0.78) 48%,
    rgba(1, 38, 62, 0.45) 100%
  );
}

@media (max-width: 767px) {
  .hero-section .hero-scrim {
    background: linear-gradient(180deg, rgba(84, 57, 41, 0.82), rgba(84, 57, 41, 0.94));
  }
}

.hero-media {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.hero-media__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center right;
  display: block;
}

.hero-section .container {
  position: relative;
  z-index: 1;
  text-align: left;
}

.hero-content {
  max-width: 36rem;
}

.hero-title {
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 1.35rem + 1.5vw, 2.5rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.18;
  margin: 0;
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.4);
}

.approche-page h1.hero-title {
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff;
  font-family: var(--font-display);
  font-weight: 600;
}

.hero-lead {
  margin: 1rem 0 1.25rem;
  max-width: 42ch;
  font-family: var(--font-body);
  font-size: clamp(1rem, 0.95rem + 0.35vw, 1.125rem);
  font-weight: 400;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.92);
}

.hero-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.hero-actions .btn-brand--primary,
.hero-actions .btn-brand--on-dark {
  text-transform: none;
  letter-spacing: 0.02em;
  height: auto;
  min-height: 48px;
  padding: 0.75rem 1.25rem;
  white-space: normal;
}

.section-padding {
  padding: clamp(52px, 6vw, 84px) 0;
  position: relative;
}

.section-header {
  text-align: center;
  margin-bottom: 56px;
}

.section-title {
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 1.35rem + 1.5vw, 2.5rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--brand-brown);
  position: relative;
  padding-bottom: 10px;
}

.section-title::after {
  content: '';
  display: block;
  width: 48px;
  height: 3px;
  margin: 14px auto 0;
  border-radius: 2px;
  background: linear-gradient(90deg, var(--brand-brown), var(--brand-gold));
  opacity: 0.95;
}

.philosophie-section {
  margin: 0 12px;
}

.philosophy-image-wrapper {
  overflow: hidden;
  border-radius: 18px;
  box-shadow: var(--shadow-xl);
}

.philosophy-content .content-title {
  color: #fff;
  font-family: var(--font-title);
}

.philosophy-content .content-text {
  color: color-mix(in srgb, var(--bank-gray-900) 88%, transparent);
  line-height: 1.8;
}

.approche-steps {
  background: var(--paper);
  margin: 0;
}

.steps-header {
  text-align: left;
  margin-bottom: 2rem;
}

.steps-eyebrow {
  margin: 0 0 0.65rem;
  font-family: var(--font-body);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--brand-gold);
}

.steps-title {
  margin: 0 0 0.75rem;
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 1.35rem + 1.5vw, 2.5rem);
  font-weight: 600;
  color: var(--brand-brown);
  text-align: left;
}

.steps-header .rule-brown {
  display: block;
  width: 48px;
  height: 3px;
  border-radius: 2px;
  background: var(--brand-gold);
  margin: 0;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.25rem;
  margin-top: 2rem;
}

.step-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 1.5rem 1.25rem 1.35rem;
  box-shadow: var(--shadow-sm);
  position: relative;
  transition: transform var(--dur-base) var(--ease), box-shadow var(--dur-base) var(--ease), border-color var(--dur-base) var(--ease);
}

.step-card:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--brand-gold) 40%, var(--line));
  box-shadow: var(--shadow-md);
}

.step-number {
  display: block;
  font-family: var(--font-display);
  font-size: 2rem;
  font-weight: 600;
  line-height: 1;
  color: var(--brand-gold);
  margin-bottom: 0.75rem;
}

.step-title {
  margin: 0 0 0.65rem;
  font-family: var(--font-display);
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--brand-navy);
  line-height: 1.3;
}

.step-desc {
  margin: 0;
  font-family: var(--font-body);
  font-size: 0.9375rem;
  line-height: 1.55;
  color: color-mix(in srgb, var(--brand-navy) 72%, #64748b);
}

.pilier-section {
  background: #fff;
  scroll-margin-top: 110px;
}

.pilier-section--alt {
  background: var(--paper);
}

.pilier-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: clamp(1.5rem, 4vw, 3rem);
  align-items: center;
}

.pilier-grid--reverse {
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
}

.pilier-grid--reverse .pilier-content {
  order: 2;
}

.pilier-grid--reverse .pilier-visual {
  order: 1;
}

.pilier-eyebrow {
  margin: 0 0 0.55rem;
  font-family: var(--font-body);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--brand-gold);
}

.pilier-title {
  margin: 0 0 0.75rem;
  font-family: var(--font-display);
  font-size: clamp(1.65rem, 1.3rem + 1.2vw, 2.35rem);
  font-weight: 600;
  color: var(--brand-brown);
  line-height: 1.2;
}

.pilier-rule {
  display: block;
  width: 48px;
  height: 3px;
  border-radius: 2px;
  background: var(--brand-gold);
  margin-bottom: 1rem;
}

.pilier-lead {
  margin: 0 0 1.25rem;
  font-family: var(--font-body);
  font-size: 1.02rem;
  line-height: 1.65;
  color: color-mix(in srgb, var(--brand-navy) 72%, #64748b);
  max-width: 38ch;
}

.pilier-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.pilier-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.65rem 0.95rem;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: color-mix(in srgb, var(--paper) 88%, #fff);
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: var(--brand-navy);
  line-height: 1.35;
}

.pilier-visual img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 18px;
  box-shadow: var(--shadow-md);
  object-fit: cover;
}

@media (max-width: 991px) {
  .pilier-grid,
.pilier-grid--reverse {
    grid-template-columns: 1fr;
  }

  .pilier-grid--reverse .pilier-content,
.pilier-grid--reverse .pilier-visual {
    order: initial;
  }

  .pilier-lead {
    max-width: none;
  }
}

.required-asterisk {
  color: var(--bank-gold);
  font-size: 1.5em;
  margin-left: 2px;
}

.required-text, .required-text-text {
  font-size: 0.8em;
  margin-left: 2px;
  font-weight: 600;
}
.required-text {
  color: #ee626b;
}

.required-text-text {
  color: var(--muted);
}

.cta-section {
  background: var(--brand-navy);
  text-align: center;
  color: var(--paper);
  padding: var(--space-9) var(--space-5);
  margin: 0;
}

.offres-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.75rem;
  max-width: 1120px;
  margin: 0 auto;
  text-align: left;
  padding-top: 1.25rem;
}

@media (max-width: 991px) {
  .offres-grid {
    grid-template-columns: 1fr;
    max-width: 420px;
  }
}

.offre-card {
  background: #fff;
  border-radius: 18px;
  padding: 2rem 1.5rem 1.5rem;
  box-shadow: 0 12px 36px rgba(2, 6, 23, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: box-shadow var(--dur-base) var(--ease), transform var(--dur-base) var(--ease);
  display: flex;
  flex-direction: column;
  color: var(--brand-ink);
  position: relative;
}

.offre-card:hover {
  box-shadow: 0 16px 44px rgba(2, 6, 23, 0.22);
  transform: translateY(-2px);
}

.offre-card--featured {
  border: 2px solid var(--brand-gold);
  box-shadow: 0 16px 44px rgba(2, 6, 23, 0.22);
}

.offre-card__badge {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.85rem;
  border-radius: 8px;
  background: var(--brand-gold);
  color: #fff;
  font-family: var(--font-body);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.offre-card__head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  margin-bottom: var(--space-4);
}

.offre-card__title {
  font-family: var(--font-display);
  font-size: 1.65rem;
  font-weight: 600;
  color: var(--brand-ink);
  margin: 0;
  text-align: left;
  line-height: 1.3;
}

.offre-card__tag {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.offre-card__price {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}

.offre-card__amount {
  font-family: var(--font-serif);
  font-size: clamp(2rem, 1.6rem + 1.2vw, 2.75rem);
  font-weight: 600;
  color: var(--brand-navy);
  line-height: 1;
}

.offre-card__amount--text {
  font-size: clamp(1.5rem, 1.3rem + 0.8vw, 2rem);
  line-height: 1.15;
}

.offre-card__currency {
  font-size: 0.9375rem;
  color: var(--muted);
  font-weight: 500;
}

.offre-card__split {
  margin: 0 0 var(--space-2);
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--muted);
  text-align: left;
}

.cta-kicker {
  margin: 0 0 var(--space-3);
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-gold-light);
}

.cta-lead {
  margin: 0 auto var(--space-7);
  max-width: 36rem;
  color: color-mix(in srgb, var(--paper) 85%, transparent);
  line-height: 1.65;
}

.cta-title {
  color: var(--paper);
}

.cta-promo {
  max-width: 40rem;
  margin: var(--space-6) auto 0;
  text-align: left;
  color: color-mix(in srgb, var(--paper) 82%, transparent);
  font-size: 0.9375rem;
}

.offre-card__list {
  list-style: none;
  padding: 0;
  margin: 0 0 1.1rem;
}

.offre-card__list li {
  position: relative;
  padding-left: 1.65rem;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
  line-height: 1.55;
  color: #475569;
}

.offre-card__list li:last-child {
  margin-bottom: 0;
}

.offre-card__list--checks li::before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 0.05em;
  color: var(--brand-gold);
  font-weight: 800;
  font-size: 1rem;
}

.offre-card__cta {
  margin-top: auto;
  padding-top: var(--space-5);
}

.btn-offre-cta {
  width: 100%;
  border-radius: var(--radius-pill) !important;
}

.cta-promo {
  max-width: 640px;
  margin: 2rem auto 0;
  padding: 0 0.5rem;
  font-size: 0.95rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.92);
  text-align: center;
}

.cta-promo .required-asterisk {
  color: var(--bank-gold-light);
  margin-right: 0.15rem;
}

.cta-title {
  color: var(--bank-white);
  margin-bottom: 24px;
}

@media (max-width: 992px) {
  .hero-title {
    font-size: 2.6rem;
  }

  .steps-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 90px 0 70px;
  }

  .hero-title {
    font-size: 2.2rem;
  }

  .steps-grid {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 1.9rem;
  }

}

@media (max-width: 576px) {
  .hero-title {
    font-size: 1.9rem;
  }

  .cta-section {
    margin: 0px;
    padding: 35px 20px;
  }

  .cta-title {
    font-size: 1.5rem;
    margin-bottom: 20px;
    line-height: 1.3;
  }
}

@media (max-width: 420px) {
  .offre-card {
    padding: 1.5rem 1.25rem 1.25rem;
  }
  .offre-card__title {
    word-break: break-word;
  }
}

@media (max-width: 400px) {
  .cta-section {
    margin: 0px;
    padding: 30px 16px;
  }

  .cta-title {
    font-size: 1.3rem;
    margin-bottom: 18px;
    line-height: 1.2;
  }
}

@media (max-width: 360px) {
  .cta-section {
    margin: 0px;
    padding: 25px 14px;
  }

  .cta-title {
    font-size: 1.2rem;
    margin-bottom: 16px;
    line-height: 1.2;
    word-break: break-word;
  }
}
</style>
